package org.teddapp.project.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import coil3.compose.AsyncImage
import org.jetbrains.compose.resources.decodeToImageBitmap
import com.mikepenz.markdown.m3.Markdown
import com.mikepenz.markdown.m3.markdownColor
import com.mikepenz.markdown.m3.markdownTypography
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import org.teddapp.project.server.ServerPublication
import org.teddapp.project.server.ServerSeries
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

// ──────────────────────────────────────────────────────────────────────
// L'aperçu : relire un contenu mis en page comme sur le site, avant de le mettre en
// ligne. Même ordre que le site — rubrique et date, titre, sous-titre, étiquettes,
// couverture, texte, chiffres, images, liens — avec les polices et couleurs de
// l'application : c'est la lecture qu'on vérifie, pas le pixel.
// ──────────────────────────────────────────────────────────────────────

/** Ce que l'aperçu montre : un contenu du serveur, ou un brouillon encore dans un formulaire. */
internal data class PreviewDoc(
    val kind: String,
    val lang: String,
    val slug: String,
    val title: String,
    val body: String,
    val subtitle: String = "",
    val summary: String = "",
    val cover: String = "",
    val images: List<Pair<String, String>> = emptyList(),
    val links: List<Pair<String, String>> = emptyList(),
    val stats: List<Pair<String, String>> = emptyList(),
    val tags: List<String> = emptyList(),
    val series: ServerSeries? = null,
    val role: String? = null,
    val publishedAtMs: Long = 0,
    /** En ligne sur le site : on peut l'y ouvrir. */
    val online: Boolean = false,
    /** Un brouillon de formulaire, pas encore enregistré. */
    val draft: Boolean = false,
) {
    val path: String
        get() = when {
            kind == "note" -> "/$lang/blog/$slug"
            kind == "portfolio_piece" -> "/$lang/portfolio/$slug"
            role == "home" -> "/$lang/"
            role == "about" -> "/$lang/" + if (lang == "en") "about/" else "a-propos/"
            else -> "/$lang/$slug"
        }

    /** La rubrique, comme le site la nomme. */
    val rubric: String
        get() = when {
            kind == "note" -> "Blog"
            kind == "portfolio_piece" -> "Portfolio"
            role == "home" -> "Accueil"
            role == "about" -> "À propos"
            else -> "Page"
        }

    /** Le temps de lecture, compté comme sur le site : 220 mots par minute. */
    val readingMinutes: Int get() = (body.split(Regex("\\s+")).count { it.isNotBlank() } / 220).coerceAtLeast(1)
}

internal fun JsonObject.text(name: String): String = this[name]?.let { runCatching { it.jsonPrimitive.contentOrNull }.getOrNull() }.orEmpty()

/** Les listes que l'appli range en JSON, parfois sous forme de texte. */
internal fun JsonObject.objects(name: String): List<JsonObject> {
    val element = this[name] ?: return emptyList()
    val array = when (element) {
        is JsonArray -> element
        is JsonPrimitive -> element.contentOrNull?.takeIf { it.trim().startsWith("[") }
            ?.let { runCatching { kotlinx.serialization.json.Json.parseToJsonElement(it) as? JsonArray }.getOrNull() }
        else -> null
    } ?: return emptyList()
    return array.mapNotNull { it as? JsonObject }
}

internal fun JsonObject.strings(name: String): List<String> {
    val element = this[name] ?: return emptyList()
    return when (element) {
        is JsonArray -> element.mapNotNull { (it as? JsonPrimitive)?.contentOrNull }
        is JsonPrimitive -> element.contentOrNull?.split(',').orEmpty()
        else -> emptyList()
    }.map { it.trim().removePrefix("#") }.filter { it.isNotEmpty() }
}

/** L'aperçu d'une publication lue sur le serveur, avec son contenu figé. */
internal fun previewOf(p: ServerPublication): PreviewDoc {
    val c = p.content ?: JsonObject(emptyMap())
    return PreviewDoc(
        kind = p.source_kind,
        lang = p.lang,
        slug = p.slug,
        title = p.title,
        body = c.text("body"),
        subtitle = c.text("subtitle"),
        summary = c.text("summary"),
        cover = c.text("cover"),
        images = c.objects("images").map { it.text("url") to it.text("caption") }.filter { it.first.isNotBlank() },
        links = c.objects("links").map { it.text("label") to it.text("url") }.filter { it.second.isNotBlank() },
        stats = c.objects("stats").map { it.text("value") to it.text("label") }.filter { it.first.isNotBlank() && it.second.isNotBlank() },
        tags = c.strings("tags"),
        series = p.series ?: (c["series"] as? JsonObject)?.let { s ->
            s.text("key").takeIf { it.isNotEmpty() }?.let { ServerSeries(it, s.text("title"), s.text("episode").toIntOrNull() ?: 1) }
        },
        role = p.role ?: (c["role"] as? JsonPrimitive)?.contentOrNull,
        publishedAtMs = p.published_at_ms,
        online = p.online,
    )
}

@Composable
internal fun PreviewDialog(model: AdminModel) {
    val doc = model.preview ?: return
    val uri = LocalUriHandler.current
    Dialog(onDismissRequest = { model.preview = null }, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        TeddCard(
            modifier = Modifier.widthIn(max = 820.dp).fillMaxWidth().padding(TeddSpace.S4).heightIn(max = 900.dp),
            title = if (doc.draft) "Aperçu du brouillon" else "Aperçu",
            icon = "eye",
            padding = PaddingValues(TeddSpace.S5),
            action = {
                Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    if (doc.online && !doc.draft) TeddButton(
                        text = "Ouvrir sur le site",
                        onClick = { runCatching { uri.openUri(model.siteUrl + doc.path) } },
                        kind = TeddButtonKind.Secondary,
                        size = org.teddapp.project.ui.TeddButtonSize.Sm,
                        icon = "globe",
                    )
                    TeddButton(text = "Fermer", onClick = { model.preview = null }, kind = TeddButtonKind.Ghost, size = org.teddapp.project.ui.TeddButtonSize.Sm)
                }
            },
        ) {
            Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                Text(doc.path, color = TeddTokens.InkSubtle, style = TeddType.MonoSm, modifier = Modifier.padding(bottom = TeddSpace.S4))
                PreviewContent(doc)
            }
        }
    }
}

/** Le contenu, dans l'ordre et les proportions du site. */
@Composable
internal fun PreviewContent(doc: PreviewDoc) {
    val page = doc.kind == "page"
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S5), modifier = Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            if (doc.series != null) Text(
                "SÉRIE · ${doc.series.title.uppercase()} · ÉPISODE ${doc.series.episode}",
                color = TeddTokens.LimeStrong, style = TeddType.Overline,
            )
            Text(
                buildList {
                    add(doc.rubric.uppercase())
                    if (doc.publishedAtMs > 0 && !page) add(formatDay(doc.publishedAtMs).uppercase())
                    if (doc.kind == "note") add("${doc.readingMinutes} MIN DE LECTURE")
                }.joinToString(" · "),
                color = TeddTokens.InkSubtle, style = TeddType.Overline,
            )
            Text(doc.title.ifBlank { "Sans titre" }, color = TeddTokens.Ink, style = TeddType.DisplayLg)
            if (doc.subtitle.isNotBlank()) Text(doc.subtitle, color = TeddTokens.InkMuted, style = TeddType.BodyLg.copy(fontSize = 19.sp, lineHeight = 28.sp))
            if (doc.tags.isNotEmpty()) FlowRow(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                doc.tags.take(4).forEach { TeddTag(it) }
            }
        }
        if (doc.cover.isNotBlank()) PreviewImage(doc.cover, "", tall = true)
        if (doc.stats.isNotEmpty()) FlowRow(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3), verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            doc.stats.take(4).forEachIndexed { i, (value, label) ->
                val tone = if (i == 0) TeddTokens.Lime else if (i == doc.stats.size - 1 && doc.stats.size > 1) TeddTokens.Ink else TeddTokens.Surface
                val ink = if (tone == TeddTokens.Ink) TeddTokens.Bg else TeddTokens.Ink
                Column(
                    modifier = Modifier.widthIn(min = 150.dp).background(tone, RoundedCornerShape(TeddTokens.RLg)).padding(TeddSpace.S4),
                    verticalArrangement = Arrangement.spacedBy(2.dp),
                ) {
                    Text(value, color = ink, style = TeddType.Title1)
                    Text(label, color = ink.copy(alpha = 0.72f), style = TeddType.Caption)
                }
            }
        }
        if (doc.body.isNotBlank()) Markdown(
            content = doc.body,
            colors = markdownColor(text = TeddTokens.Ink),
            typography = markdownTypography(
                h1 = TeddType.Title1,
                h2 = TeddType.Title1.copy(fontSize = 24.sp, lineHeight = 30.sp),
                h3 = TeddType.Title2,
                text = TeddType.BodyLg,
                paragraph = TeddType.BodyLg,
                ordered = TeddType.BodyLg,
                bullet = TeddType.BodyLg,
                list = TeddType.BodyLg,
            ),
        ) else Text("Pas de texte.", color = TeddTokens.InkSubtle, style = TeddType.Body)
        if (doc.images.isNotEmpty()) Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            doc.images.forEach { (url, caption) -> PreviewImage(url, caption, tall = false) }
        }
        if (doc.links.isNotEmpty()) FlowRow(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            doc.links.forEach { (label, url) ->
                Box(Modifier.background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RFull)).padding(horizontal = TeddSpace.S4, vertical = TeddSpace.S2)) {
                    Text((label.ifBlank { url }) + " ↗", color = TeddTokens.Ink, style = TeddType.Label, maxLines = 1, overflow = TextOverflow.Ellipsis)
                }
            }
        }
    }
}

/**
 * Comment lire une image déposée depuis l'éditeur (`file:<id>`) : ses octets, par le
 * serveur. Absent, l'aperçu dit seulement d'où l'image vient.
 */
internal val LocalSiteImageLoader = androidx.compose.runtime.staticCompositionLocalOf<(suspend (String) -> ByteArray?)?> { null }

/** Une image du site : par le réseau quand c'est une adresse, par le serveur quand elle y est déposée, sinon un cadre qui dit d'où elle vient. */
@Composable
internal fun PreviewImage(url: String, caption: String, tall: Boolean) {
    val shape = RoundedCornerShape(TeddTokens.RLg)
    val frame = Modifier.fillMaxWidth().heightIn(min = if (tall) 180.dp else 120.dp, max = if (tall) 360.dp else 300.dp).clip(shape).background(TeddTokens.SurfaceSunken, shape)
    val loader = LocalSiteImageLoader.current
    val fileId = url.removePrefix("file:").takeIf { url.startsWith("file:") && it.isNotBlank() }
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
        if (url.startsWith("http://") || url.startsWith("https://")) {
            AsyncImage(model = url, contentDescription = caption.ifBlank { null }, contentScale = ContentScale.Crop, modifier = frame)
        } else if (fileId != null && loader != null) {
            SiteImage(fileId, loader, caption, frame)
        } else {
            Box(frame, contentAlignment = Alignment.Center) {
                Text(
                    if (url.startsWith("file:")) "Image de l'appareil : le site la convertira à sa construction." else "Image : $url",
                    color = TeddTokens.InkSubtle, style = TeddType.Caption, modifier = Modifier.padding(TeddSpace.S4),
                )
            }
        }
        if (caption.isNotBlank()) Text(caption, color = TeddTokens.InkSubtle, style = TeddType.Caption)
    }
}

/** Une image déposée sur le serveur, décodée par Compose : on a déjà les octets. */
@OptIn(org.jetbrains.compose.resources.ExperimentalResourceApi::class)
@Composable
internal fun SiteImage(id: String, loader: suspend (String) -> ByteArray?, caption: String, modifier: Modifier) {
    var bitmap by androidx.compose.runtime.remember(id) { androidx.compose.runtime.mutableStateOf<androidx.compose.ui.graphics.ImageBitmap?>(null) }
    var failed by androidx.compose.runtime.remember(id) { androidx.compose.runtime.mutableStateOf(false) }
    androidx.compose.runtime.LaunchedEffect(id) {
        bitmap = kotlinx.coroutines.withContext(kotlinx.coroutines.Dispatchers.Default) {
            runCatching { loader(id)?.decodeToImageBitmap() }.getOrNull()
        }
        failed = bitmap == null
    }
    val b = bitmap
    if (b != null) {
        androidx.compose.foundation.Image(bitmap = b, contentDescription = caption.ifBlank { null }, contentScale = ContentScale.Crop, modifier = modifier)
    } else {
        Box(modifier, contentAlignment = Alignment.Center) {
            Text(if (failed) "Image indisponible" else "Chargement…", color = TeddTokens.InkSubtle, style = TeddType.Caption)
        }
    }
}
